import { useCallback, useMemo } from "react";
import { useAuthStore } from "../stores/auth.store";
import { useOrganizationStore } from "../stores/organization.store";
import {
  canAccessOrg,
  getSitesForOrg,
  isNavGroup,
  resolveNavPermission,
  PERM_USER_WRITE,
  PERM_ROLE_ASSIGN,
  PERM_ADMIN_USER_LIST,
  USER_MANAGEMENT_ROLES,
} from "../auth";
import type { NavItemDef, MenuEntry } from "../auth";
import { isReadOnlyAdmin } from "../utils/auth.helpers";

export function useAuthPermissions() {
  const {
    tokenPayload,
    permissionList,
    isPlatformAdmin: platformAdmin,
    user,
    canAccessPermission,
  } = useAuthStore();
  const { selectedOrganization } = useOrganizationStore();

  const orgId = selectedOrganization?.id ?? null;
  const readOnly = useMemo(
    () => isReadOnlyAdmin(user, tokenPayload, permissionList),
    [user, tokenPayload, permissionList],
  );

  const can = useCallback(
    (required: string, legacyRoles?: readonly string[]) =>
      canAccessPermission(required, legacyRoles, orgId),
    [canAccessPermission, orgId],
  );

  const canMutateAction = useCallback(
    (required: string, legacyRoles?: readonly string[]) =>
      !readOnly && can(required, legacyRoles),
    [readOnly, can],
  );

  const canSeeNavItem = useCallback(
    (item: NavItemDef): boolean => {
      if (platformAdmin) {
        return true;
      }

      if (item.accessType === "platform") {
        return platformAdmin;
      }

      if (item.accessType === "orgMembership") {
        return canAccessOrg(tokenPayload, permissionList, orgId);
      }

      if (item.accessType === "platformScoped") {
        const perm = resolveNavPermission(item, orgId);
        if (!perm) {
          return platformAdmin;
        }
        return can(perm);
      }

      if (item.accessType === "orgScoped") {
        const perm = resolveNavPermission(item, orgId);
        if (!perm) {
          return false;
        }
        if (item.entity === "user" && item.action === "list" && can(PERM_ADMIN_USER_LIST)) {
          return true;
        }
        return can(perm, item.legacyRoles);
      }

      return false;
    },
    [platformAdmin, tokenPayload, permissionList, orgId, can],
  );

  const filterMenuEntries = useCallback(
    (entries: MenuEntry[]): MenuEntry[] => {
      return entries
        .map((entry) => {
          if (isNavGroup(entry)) {
            const visibleSubItems = entry.subItems.filter(canSeeNavItem);
            if (visibleSubItems.length === 0) {
              return null;
            }
            return { ...entry, subItems: visibleSubItems };
          }
          return canSeeNavItem(entry) ? entry : null;
        })
        .filter((e): e is MenuEntry => e !== null);
    },
    [canSeeNavItem],
  );

  const sitesForOrg = useCallback(
    (targetOrgId: string) => getSitesForOrg(tokenPayload, targetOrgId),
    [tokenPayload],
  );

  const canManageTeam = useCallback(
    (targetOrgId: string) => {
      if (platformAdmin) {
        return !readOnly;
      }
      return canMutateAction(PERM_USER_WRITE(targetOrgId), USER_MANAGEMENT_ROLES);
    },
    [platformAdmin, readOnly, canMutateAction],
  );

  const canAssignSites = useCallback(
    (targetOrgId: string) => {
      if (platformAdmin) {
        return !readOnly;
      }
      return (
        canMutateAction(PERM_ROLE_ASSIGN(targetOrgId), USER_MANAGEMENT_ROLES) ||
        (canMutateAction(PERM_USER_WRITE(targetOrgId), USER_MANAGEMENT_ROLES) &&
          can(`recyclee.${targetOrgId}.*`))
      );
    },
    [platformAdmin, readOnly, canMutateAction, can],
  );

  const canAccessOrgContext = useCallback(
    (targetOrgId: string) =>
      platformAdmin || canAccessOrg(tokenPayload, permissionList, targetOrgId),
    [platformAdmin, tokenPayload, permissionList],
  );

  return {
    orgId,
    user,
    tokenPayload,
    permissionList,
    isPlatformAdmin: platformAdmin,
    isReadOnly: readOnly,
    can,
    canMutate: canMutateAction,
    canSeeNavItem,
    filterMenuEntries,
    canManageTeam,
    canAssignSites,
    canAccessOrg: canAccessOrgContext,
    sitesForOrg,
  };
}
